import React from 'react';
import { IconTrash, IconUser, IconUserPlus } from '@tabler/icons-react';
import VehicleEmployeeSearchField from './VehicleEmployeeSearchField';

// One array, two row shapes: { employee } for staff, or name/designation/org for guests.

export const emptyStaffRow = () => ({ kind: 'staff', employee: null, name: '', designation: '', organization: '' });
export const emptyExternalRow = () => ({ kind: 'external', employee: null, name: '', designation: '', organization: '' });

// Drops incomplete rows rather than sending them half-filled.
export function passengersToPayload(rows) {
    return rows.reduce((acc, row) => {
        if (row.kind === 'staff') {
            if (row.employee?.id) acc.push({ employee: row.employee.id });
        } else if (row.name?.trim()) {
            acc.push({
                name: row.name.trim(),
                designation: row.designation?.trim() || '',
                organization: row.organization?.trim() || '',
            });
        }
        return acc;
    }, []);
}

// Detail payload → editable rows.
export function passengersFromApi(passengers) {
    return (passengers || []).map(p => (
        p.employee
            ? {
                kind: 'staff',
                employee: { id: p.employee, name: p.name || '', organization: p.organization || '' },
                name: p.name || '',
                designation: p.designation || '',
                organization: p.organization || '',
            }
            : {
                kind: 'external',
                employee: null,
                name: p.name || '',
                designation: p.designation || '',
                organization: p.organization || '',
            }
    ));
}

export default function PassengerEditor({ value = [], onChange, orgId, disabled = false, error }) {
    const update = (index, patch) => {
        onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const remove = (index) => onChange(value.filter((_, i) => i !== index));

    return (
        <div>
            <div className="flex items-center justify-between mb-10">
                <label className="text-sm font-medium">Passengers</label>
                {!disabled && (
                    <div className="flex gap-5">
                        <button
                            type="button"
                            onClick={() => onChange([...value, emptyStaffRow()])}
                            className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5"
                        >
                            <IconUser size={14} /> Add staff
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange([...value, emptyExternalRow()])}
                            className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5"
                        >
                            <IconUserPlus size={14} /> Add guest
                        </button>
                    </div>
                )}
            </div>

            {value.length === 0 ? (
                <p className="text-xs text-text-secondary border border-dashed border-border rounded-[4px] px-10 py-15 text-center">
                    No passengers added — you will be listed as the sole passenger.
                </p>
            ) : (
                <div className="flex flex-col gap-10">
                    {value.map((row, index) => (
                        <div key={index} className="border border-border rounded-[4px] p-10">
                            <div className="flex items-center justify-between mb-8">
                                <span className="text-xs font-medium text-text-secondary">
                                    Passenger {index + 1} · {row.kind === 'staff' ? 'Staff' : 'Guest'}
                                </span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-text-secondary hover:text-danger"
                                    >
                                        <IconTrash size={14} />
                                    </button>
                                )}
                            </div>

                            {row.kind === 'staff' ? (
                                <VehicleEmployeeSearchField
                                    orgId={orgId}
                                    value={row.employee}
                                    onSelect={(emp) => update(index, { employee: emp })}
                                    onClear={() => update(index, { employee: null })}
                                    placeholder="Search staff by name..."
                                    disabled={disabled}
                                />
                            ) : (
                                <div className="grid md:grid-cols-3 grid-cols-1 gap-8">
                                    <input
                                        type="text"
                                        value={row.name}
                                        disabled={disabled}
                                        onChange={(e) => update(index, { name: e.target.value })}
                                        placeholder="Full name"
                                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                    />
                                    <input
                                        type="text"
                                        value={row.designation}
                                        disabled={disabled}
                                        onChange={(e) => update(index, { designation: e.target.value })}
                                        placeholder="Designation"
                                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                    />
                                    <input
                                        type="text"
                                        value={row.organization}
                                        disabled={disabled}
                                        onChange={(e) => update(index, { organization: e.target.value })}
                                        placeholder="Organization"
                                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {error && <p className="text-danger text-xs mt-5">{error}</p>}
            {!disabled && value.length > 0 && (
                <p className="text-xs text-text-secondary mt-8">
                    Saving replaces the whole passenger list — add yourself if you are travelling.
                </p>
            )}
        </div>
    );
}
